import "../../assets/styles/ReportsView.css";
import OpportunityTable from "./OpportunityTable";
import { useTooltips } from "../../hooks/useTooltips";
import { useState, useEffect } from "react";
// Import Bootstrap JS
import * as bootstrap from "bootstrap";

const OpportunityView = ({
  opportunityData,
  totalCount,
  loading,
  handleSort,
  sortOrder,
  setLoading,
  getOpportunities,
}) => {
  // Add state for tracking loading overlay
  const [isRefreshing, setIsRefreshing] = useState(false);

  // Use our custom tooltip hook instead of direct Bootstrap initialization
  // Only initialize tooltips when not refreshing to avoid tooltip errors
  useEffect(() => {
    if (!isRefreshing) {
      const tooltipTriggerList = document.querySelectorAll(
        '[data-bs-toggle="tooltip"]'
      );
      tooltipTriggerList.forEach((el) => {
        // Make sure any existing tooltip is disposed first
        const existingTooltip = bootstrap.Tooltip.getInstance(el);
        if (existingTooltip) {
          existingTooltip.dispose();
        }

        // Create new tooltip
        new bootstrap.Tooltip(el, {
          placement: "top",
          html: true,
        });
      });
    }

    // Cleanup function to dispose tooltips
    return () => {
      const tooltipTriggerList = document.querySelectorAll(
        '[data-bs-toggle="tooltip"]'
      );
      tooltipTriggerList.forEach((el) => {
        const tooltip = bootstrap.Tooltip.getInstance(el);
        if (tooltip) {
          tooltip.dispose();
        }
      });
    };
  }, [opportunityData, isRefreshing]);

  const handleRefresh = async () => {
    try {
      // Dispose tooltips before showing overlay
      const tooltipTriggerList = document.querySelectorAll(
        '[data-bs-toggle="tooltip"]'
      );
      tooltipTriggerList.forEach((el) => {
        const tooltip = bootstrap.Tooltip.getInstance(el);
        if (tooltip) {
          tooltip.dispose();
        }
      });

      setIsRefreshing(true); // Show loading overlay
      // Don't set loading here since we're using our own overlay
      await getOpportunities(true); // Pass true to indicate force refresh
    } catch (error) {
      console.error("Error refreshing opportunities:", error);
    } finally {
      setIsRefreshing(false); // Hide loading overlay when done
    }
  };

  const handleGetZohoRecords = async () => {
    try {
      // Dispose tooltips before showing overlay
      const tooltipTriggerList = document.querySelectorAll(
        '[data-bs-toggle="tooltip"]'
      );
      tooltipTriggerList.forEach((el) => {
        const tooltip = bootstrap.Tooltip.getInstance(el);
        if (tooltip) {
          tooltip.dispose();
        }
      });

      setIsRefreshing(true); // Show loading overlay
      // Don't set loading here since we're using our own overlay

      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/zoho/DealsForced`
      );
      console.log("Zoho records fetched:", response);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      // Check if the response has the correct content type
      const contentType = response.headers.get("content-type");
      if (!contentType || !contentType.includes("application/json")) {
        handleRefresh(); //refresh the page no matter what the response from zoho
        throw new Error("Response is not JSON format");
      }

      // Try to parse the JSON
      try {
        const data = await response.json();
        console.log("Zoho records fetched:", data);
        handleRefresh();
      } catch (jsonError) {
        console.error("Error parsing JSON response:", jsonError);
        handleRefresh(); //refresh the page no matter what the response from zoho
        throw new Error("Invalid JSON response");
      }
    } catch (error) {
      console.error(
        "Error fetching Zoho records. Could be no records to fetch:",
        error
      );
      handleRefresh(); //refresh the page no matter what the response from zoho
    }
  };

  return (
    <div className="opportunity-view position-relative">
      {/* Loading Overlay */}
      {isRefreshing && (
        <div
          className="position-absolute w-100 h-100 d-flex justify-content-center align-items-center"
          style={{
            backgroundColor: "rgba(255, 255, 255, 0.7)",
            zIndex: 1000,
            top: 0,
            left: 0,
          }}
        >
          <div className="text-center">
            <div
              className="spinner-border text-primary"
              role="status"
              style={{ width: "3rem", height: "3rem" }}
            >
              <span className="visually-hidden">Loading...</span>
            </div>
            <div className="mt-2 fw-bold">Refreshing data, please wait...</div>
          </div>
        </div>
      )}

      <div className="opportunity-header">
        <p className="opportunities-total">Total Records: {totalCount}</p>
        <p>
          <button
            type="button"
            className="btn btn-secondary me-2"
            onClick={handleGetZohoRecords}
            data-bs-toggle="tooltip"
            data-bs-placement="top"
            data-bs-html="true"
            data-bs-title="Force Zoho Refresh"
            title="Force Zoho Refresh"
            disabled={isRefreshing}
          >
            <i className="bi bi-cloud-download"> Force Zoho Refresh</i>
          </button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={handleRefresh}
            data-bs-toggle="tooltip"
            data-bs-placement="top"
            data-bs-html="true"
            data-bs-title="Refresh Opportunities List"
            title="Refresh Opportunities List"
            disabled={isRefreshing}
          >
            <i className="bi bi-arrow-clockwise"> Refresh Opportunities List</i>
          </button>
        </p>
      </div>
      <div className="table-wrapper">
        <OpportunityTable
          data={opportunityData}
          loading={loading && !isRefreshing} // Only show table loading if not showing overlay
          setLoading={setLoading}
          requestSort={handleSort}
          sortOrder={sortOrder}
        />
      </div>
    </div>
  );
};

export default OpportunityView;
